/**
 * Where this copy of the app is running: the hosted site (served by the server itself), or one of
 * the installed apps (Android via Capacitor, Windows via Tauri) that carry the web app inside and
 * connect to the server address typed at sign-in. Both installed apps load their pages from an
 * http(s) address of their own (https://localhost, http://tauri.localhost), so the page protocol
 * alone can't tell them apart from the hosted site.
 */
export type NativeShell = "android" | "windows";

interface ShellGlobals {
  Capacitor?: { isNativePlatform?: () => boolean };
  __TAURI_INTERNALS__?: unknown;
}

export function nativeShell(): NativeShell | null {
  const w = window as unknown as ShellGlobals;
  if (typeof w.Capacitor?.isNativePlatform === "function" && w.Capacitor.isNativePlatform()) return "android";
  if (w.__TAURI_INTERNALS__ !== undefined) return "windows";
  return null;
}

/** Served by a Backflow server: the public site, portal and emailed links live here, and the API is this origin. */
export function isHosted(): boolean {
  return window.location.protocol.startsWith("http") && nativeShell() === null;
}

const SERVER_KEY = "backflow.server";

/** The server address the installed app last connected to (or the one it was built for). */
export function rememberedServer(): string {
  try {
    const saved = localStorage.getItem(SERVER_KEY);
    if (saved) return saved;
  } catch {
    // Storage blocked: fall through to the built-in address.
  }
  return import.meta.env.VITE_BACKFLOW_SERVER ?? "";
}

export function rememberServer(base: string): void {
  try {
    localStorage.setItem(SERVER_KEY, base);
  } catch {
    // Not remembered; the address is asked again next time.
  }
}

const EXTERNAL = /^(https?|mailto|tel|sms):/i;

/**
 * Once at start in an installed app: links that leave the app (card checkout, email, phone,
 * maps) open in the phone's or PC's own apps, Print uses the system print screen, and offline
 * work is kept out of the reach of storage clean-up.
 */
export async function installNativeShell(): Promise<void> {
  const shell = nativeShell();
  if (!shell) return;
  void navigator.storage?.persist?.().catch(() => false);
  if (shell === "android") {
    // Android's web view has no print of its own; the app hands the page to Android's print screen.
    const { registerPlugin } = await import("@capacitor/core");
    const printer = registerPlugin<{ print(options: { name: string }): Promise<void> }>("BackflowPrint");
    window.print = () => void printer.print({ name: document.title || "ABC" });
    // Other links already open outside: the Android app hands any address that isn't its own to the system.
    return;
  }
  const { invoke } = await import("@tauri-apps/api/core");
  const openOutside = (url: string) => void invoke("open_external", { url }).catch(() => undefined);
  const isOutside = (url: URL) => EXTERNAL.test(url.protocol) && url.origin !== window.location.origin;
  window.open = ((url?: string | URL) => {
    if (url !== undefined) {
      const target = new URL(String(url), window.location.href);
      if (isOutside(target)) openOutside(target.href);
    }
    return null;
  }) as typeof window.open;
  document.addEventListener(
    "click",
    (e) => {
      const a = (e.target as Element | null)?.closest?.("a[href]");
      if (!(a instanceof HTMLAnchorElement) || a.hasAttribute("download")) return;
      const target = new URL(a.href, window.location.href);
      if (!isOutside(target)) return;
      e.preventDefault();
      openOutside(target.href);
    },
    true,
  );
}
